import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FormActionsComponent } from '../../components/form-actions/form-actions.component';
import { FormInputFieldComponent } from '../../components/form-input-field/form-input-field.component';
import { FormTextareaFieldComponent } from '../../components/form-textarea-field/form-textarea-field.component';
import { FormLayoutComponent } from '../../components/form-layout/form-layout.component';
import { LoadingButtonComponent } from '../../components/loading-button/loading-button.component';
import { UnauthPageIntroComponent } from '../../components/unauth-page-intro/unauth-page-intro.component';
import { UnauthenticatedPageComponent } from '../../components/unauthenticated-page/unauthenticated-page.component';
import { PublicLeadSubmissionErrorDialogService } from '../../services/public-lead-submission-error-dialog.service';
import { PublicLeadSubmissionService } from '../../services/public-lead-submission.service';
import { RecaptchaService } from '../../services/recaptcha.service';
import { UnauthenticatedForwardNavService } from '../../services/unauthenticated-forward-nav.service';
import { PUBLIC_MARKETING_RUNTIME_CONFIG } from '../../tokens/public-marketing-runtime.token';
import { IonNote, ViewWillEnter } from '@ionic/angular/standalone';
import {
  PUBLIC_LEAD_MAX_EMAIL,
  PUBLIC_LEAD_MAX_MESSAGE,
  PUBLIC_LEAD_MAX_NAME,
  PUBLIC_LEAD_MAX_ORG,
  publicLeadEmailValidator,
  trimmedRequired,
} from '../about-project/public-lead-form.validators';

@Component({
  selector: 'app-register-interest',
  standalone: true,
  imports: [
    UnauthenticatedPageComponent,
    UnauthPageIntroComponent,
    FormActionsComponent,
    FormInputFieldComponent,
    FormTextareaFieldComponent,
    FormLayoutComponent,
    LoadingButtonComponent,
    ReactiveFormsModule,
    IonNote,
  ],
  templateUrl: './register-interest.component.html',
  styleUrl: '../about-project/about-project.component.css',
})
export class RegisterInterestComponent implements ViewWillEnter {
  private readonly recaptcha = inject(RecaptchaService);
  private readonly submissions = inject(PublicLeadSubmissionService);
  private readonly submitErrorDialog = inject(PublicLeadSubmissionErrorDialogService);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly marketing = inject(PUBLIC_MARKETING_RUNTIME_CONFIG);
  readonly unauthNav = inject(UnauthenticatedForwardNavService);

  readonly registerInterestUrl = this.marketing.registerInterestUrl?.trim() ?? '';
  readonly pageTitle = 'Register interest';

  readonly showInlineForm = !!(this.marketing.apiUrl?.trim() && this.recaptcha.isConfigured());

  readonly maxNameLen = PUBLIC_LEAD_MAX_NAME;
  readonly maxEmailLen = PUBLIC_LEAD_MAX_EMAIL;
  readonly maxOrgLen = PUBLIC_LEAD_MAX_ORG;
  readonly maxMessageLen = PUBLIC_LEAD_MAX_MESSAGE;

  readonly registerForm = this.fb.nonNullable.group({
    name: ['', [trimmedRequired, Validators.maxLength(PUBLIC_LEAD_MAX_NAME)]],
    email: [
      '',
      [trimmedRequired, Validators.maxLength(PUBLIC_LEAD_MAX_EMAIL), publicLeadEmailValidator],
    ],
    organisation: ['', [Validators.maxLength(PUBLIC_LEAD_MAX_ORG)]],
    message: ['', [Validators.maxLength(PUBLIC_LEAD_MAX_MESSAGE)]],
  });

  submitting = false;
  errorMessage = '';
  submitted = false;

  ionViewWillEnter(): void {
    this.submitting = false;
  }

  fieldError(controlName: 'name' | 'email' | 'organisation' | 'message'): string {
    if (!this.submitted) {
      return '';
    }
    const c = this.registerForm.get(controlName);
    if (!c?.errors) {
      return '';
    }
    const e = c.errors;
    if (e['required']) {
      if (controlName === 'name') {
        return 'Enter your name.';
      }
      if (controlName === 'email') {
        return 'Enter your email.';
      }
      return '';
    }
    if (e['maxlength']) {
      const max = e['maxlength'].requiredLength as number;
      return `Must be at most ${max} characters.`;
    }
    if (e['email']) {
      return 'Enter a valid email address.';
    }
    return '';
  }

  async onSubmit(): Promise<void> {
    this.errorMessage = '';
    this.submitted = true;

    if (!this.showInlineForm) {
      return;
    }

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.errorMessage = 'Please correct the highlighted fields.';
      return;
    }

    const { name, email, organisation, message } = this.registerForm.getRawValue();
    const nameT = name.trim();
    const emailT = email.trim().toLowerCase();
    const organisationT = organisation.trim();
    const messageT = message.trim();

    this.submitting = true;
    try {
      const recaptchaToken = await this.recaptcha.execute('register_interest');
      await this.submissions.submit({
        kind: 'register_interest',
        recaptchaToken,
        name: nameT,
        email: emailT,
        organisation: organisationT,
        message: messageT,
      });
      this.registerForm.reset(
        { name: '', email: '', organisation: '', message: '' },
        { emitEvent: false },
      );
      this.submitted = false;
      const navigated = await this.router.navigateByUrl('/register-interest/thanks', {
        replaceUrl: true,
      });
      if (!navigated) {
        this.submitting = false;
        this.errorMessage = 'Could not open the thank-you page. Please try again.';
      }
    } catch (e) {
      this.submitting = false;
      this.errorMessage =
        e instanceof Error ? e.message : 'Something went wrong. Please try again.';
      await this.submitErrorDialog.show(e);
    }
  }
}
